import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkImportModal = ({ isOpen, onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [importStatus, setImportStatus] = useState(null);

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFile(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFile(e?.target?.files?.[0]);
    }
  };

  const handleFile = (selectedFile) => {
    if (
      selectedFile?.type === 'text/csv' || selectedFile?.name?.endsWith('.csv')
    ) {
      setFile(selectedFile);
      setImportStatus(null);
    } else {
      setImportStatus({
        type: 'error',
        message: 'Please upload a valid CSV file',
      });
    }
  };

  const handleImport = () => {
    if (!file) return;

    setImportStatus({
      type: 'success',
      message: `Successfully imported ${file?.name}. Processing 15 users...`,
    });

    setTimeout(() => {
      onImport(file);
      handleClose();
    }, 2000);
  };

  const handleDownloadTemplate = () => {
    const csvContent = `Name,Email,Role,Department,MFA Enabled
John Smith,john.smith@example.com,Ops,Operations,true
Sarah Johnson,sarah.johnson@example.com,Finance,Finance,false
Michael Brown,michael.brown@example.com,Admin,Administration,true`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const handleClose = () => {
    setFile(null);
    setImportStatus(null);
    setDragActive(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="relative bg-card rounded-lg shadow-lg w-full max-w-2xl mx-4">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Bulk User Import
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Import multiple users from CSV file
              </p>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-accent/10 hover:text-accent transition-colors"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Icon
              name="Upload"
              size={48}
              className="mx-auto text-muted-foreground mb-4"
            />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {file ? file?.name : 'Drop CSV file here'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse from your computer
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileInput}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button variant="outline" size="sm" iconName="FileUp" asChild>
                <span>Choose File</span>
              </Button>
            </label>
          </div>

          {importStatus && (
            <div
              className={`p-4 rounded-lg flex items-start gap-3 ${
                importStatus?.type === 'success' ?'bg-success/10 text-success' :'bg-error/10 text-error'
              }`}
            >
              <Icon
                name={
                  importStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'
                }
                size={20}
                className="flex-shrink-0 mt-0.5"
              />
              <p className="text-sm">{importStatus?.message}</p>
            </div>
          )}

          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-start gap-3 mb-4">
              <Icon
                name="Info"
                size={20}
                className="text-primary flex-shrink-0 mt-0.5"
              />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-2">
                  CSV Format Requirements:
                </p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>
                    Required columns: Name, Email, Role, Department, MFA Enabled
                  </li>
                  <li>Role must be one of: Admin, Ops, Finance, RNR</li>
                  <li>MFA Enabled must be: true or false</li>
                  <li>Email addresses must be unique and valid</li>
                  <li>Maximum 100 users per import</li>
                </ul>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={handleDownloadTemplate}
              fullWidth
            >
              Download CSV Template
            </Button>
          </div>
        </div>

        <div className="p-6 border-t border-border flex items-center justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            iconName="Upload"
            onClick={handleImport}
            disabled={!file}
          >
            Import Users
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;